import {
  getPermissionScope,
  type PermissionMap,
  PermissionKey,
  PermissionScope,
  type PermissionScopeEnum,
} from "@fitness-app/data-models";

/**
 * Jak szeroko dana osoba widzi listę klientek — wyliczone ze scope'u
 * `clients.read`, nie z roli.
 *
 * Do tej pory każdy ekran z listą klientek miał własny wariant tego samego
 * warunku po rolach (`isRegularTrainer`, `isOwner || isSharedTrainer`,
 * a po cutoverze doszywane `&& !isAdministration`). Efekt: nowa rola albo
 * nadanie uprawnienia per osoba wymagały poprawki w pięciu plikach i za
 * każdym razem któryś zostawał w tyle.
 *
 * Odwzorowanie ról sprzed migracji: REGULAR_TRAINER = ASSIGNED,
 * SHARED_TRAINER = TEAM, ACCOUNT_OWNER / administracja / sprzedaż = ALL.
 */
export interface ClientsAccess {
  canReadClients: boolean;
  /** Kolumna i filtr „Trener" mają sens dopiero powyżej własnego przydziału. */
  canFilterByTrainer: boolean;
  /**
   * Zapytania muszą dopiąć `assignedTrainerId` własnego członka — inaczej
   * osoba zobaczy klientki, których RLS i tak jej nie odda.
   */
  pinnedToOwnClients: boolean;
  /** Widzi cały tenant, bez przecięcia z przypisaniami. */
  readsWholeTenant: boolean;
  /**
   * Przełącznik „tylko przypisani trenerzy" ma sens wyłącznie przy TEAM:
   * przy ASSIGNED nie ma czego przełączać, przy ALL i tak widać wszystko.
   */
  canToggleAssignedTrainers: boolean;
  scope: PermissionScopeEnum;
}

export const createClientsAccess = (permissions: PermissionMap): ClientsAccess => {
  const scope = getPermissionScope(permissions, PermissionKey.CLIENTS_READ);

  return {
    canReadClients: scope !== PermissionScope.NONE,
    canFilterByTrainer: scope === PermissionScope.TEAM || scope === PermissionScope.ALL,
    pinnedToOwnClients: scope === PermissionScope.ASSIGNED,
    readsWholeTenant: scope === PermissionScope.ALL,
    canToggleAssignedTrainers: scope === PermissionScope.TEAM,
    scope,
  };
};

/**
 * `assignedTrainerId` dopinany do zapytania o listę klientek: własny członek
 * przy ASSIGNED, w pozostałych przypadkach to, co wybrano w filtrze.
 */
export const getClientListTrainerId = (
  access: ClientsAccess,
  memberId: string | null | undefined,
  selectedTrainerId: string | null,
): string | null => (access.pinnedToOwnClients ? (memberId ?? null) : selectedTrainerId);
